// WHAT: Einstellungs-Panel zur Konfiguration sichtbarer Dashboard-Widgets (per Kategorie gruppiert)
// WHY:  Ermöglicht es Nutzer:innen, das Core-Dashboard modular zusammenzustellen, ohne den Code anzupassen
// DOMAIN: core → dashboard → controls
// DEPENDENCIES: `useDashboardWidgets`-Hook, `widgetRegistry`, TracQ UI-Komponenten (Modal, Button, Card, Heading, Text)
// RISKS:
//   - NOTE: Aktuell nur Sichtbarkeit/Reset, zukünftige Drag&Drop-Umbauten müssen die Struktur dieses Panels berücksichtigen

import { Settings } from "lucide-react";
import { type FC, useState } from "react";

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Heading,
  Text,
  Card,
} from "@/components/ui";

import { useDashboardWidgets } from "../../hooks/useDashboardWidgets";
import { widgetRegistry } from "../../store/widgetRegistry";
import { WidgetVisibilityToggle } from "./WidgetVisibilityToggle";

/**
 * WHAT:
 *   Modal-basiertes Settings-Panel, das alle registrierten Dashboard-Widgets nach Kategorie gruppiert
 *   anzeigt und deren Sichtbarkeit über `WidgetVisibilityToggle` steuert.
 *
 * WHY:
 *   Bietet eine zentrale, UI-getriebene Möglichkeit, das Core-Dashboard zu personalisieren und bei Bedarf
 *   auf den Standardzustand zurückzusetzen.
 *
 * PROPS:
 *   - keine externen Props — die Komponente verwaltet nur ihren eigenen Open/Close-Status.
 *
 * BEHAVIOR:
 *   - Liest alle Widgets aus `widgetRegistry`, gruppiert sie nach `category` und rendert pro Widget einen Toggle.
 *   - Nutzt `useDashboardWidgets`, um aktuelle Konfiguration und `resetWidgets` zu beziehen.
 *   - Schließt das Modal beim Reset oder beim expliziten „Close“-Klick.
 *
 * RISKS:
 *   - NOTE: Bei Änderungen an der Widget-Registry (neue Kategorien/IDs) muss die Gruppierungslogik konsistent bleiben.
 *
 * @returns {JSX.Element} Dashboard settings modal component
 *
 */
export const DashboardSettings: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { resetWidgets } = useDashboardWidgets();
  const allWidgets = widgetRegistry.getAll();

  const groupedWidgets: Record<string, typeof allWidgets> = {};
  for (const widget of allWidgets) {
    const category = widget.category;
    groupedWidgets[category] ??= [];
    groupedWidgets[category].push(widget);
  }

  return (
    <>
      <Button
        aria-label="Dashboard settings"
        onClick={() => {
          setIsOpen(true);
        }}
        size="sm"
        variant="outline"
      >
        <Settings className="w-4 h-4 mr-2" />
        Settings
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        size="lg"
      >
        <ModalHeader>
          <Heading level={2}>Dashboard Settings</Heading>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-6">
            {Object.entries(groupedWidgets).map(([category, categoryWidgets]) => (
              <div key={category}>
                <Heading className="text-sm font-semibold text-gray-300 mb-3 capitalize" level={3}>
                  {category}
                </Heading>
                <div className="space-y-2">
                  {categoryWidgets.map((widget) => (
                    <Card
                      className="flex items-center justify-between"
                      key={widget.id}
                      padding="sm"
                      variant="default"
                    >
                      <div className="flex-1">
                        <Text className="text-sm font-medium text-white">{widget.name}</Text>
                        {widget.description ? (
                          <Text className="text-xs text-gray-400 mt-0.5">{widget.description}</Text>
                        ) : null}
                      </div>
                      <WidgetVisibilityToggle widgetId={widget.id} />
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              resetWidgets();
              setIsOpen(false);
            }}
            variant="outline"
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={() => {
              setIsOpen(false);
            }}
            variant="primary"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
